import { authApi, TokenResponse } from "@/api/auth-api";

interface TokenPayload {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  type: string;
  exp: number;
  iat: number;
  iss: string;
  aud: string;
}

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private tokenExpiryTimeout: NodeJS.Timeout | null = null;

  constructor() {
    // Try to restore refresh token from localStorage on init
    const storedRefreshToken = localStorage.getItem("refreshToken");
    if (storedRefreshToken) {
      this.refreshToken = storedRefreshToken;
    }
  }

  setTokens(tokens: TokenResponse) {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;

    // Store refresh token in localStorage for persistence across sessions
    if (this.refreshToken) {
      localStorage.setItem("refreshToken", this.refreshToken);
    }

    // Set up automatic refresh before token expires
    this.scheduleTokenRefresh();
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  async getValidAccessToken(): Promise<string | null> {
    // If token is expired or about to expire, refresh it
    if (this.isTokenExpired()) {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        // If refresh fails, clear tokens
        this.clearTokens();
        throw error;
      }
    }
    return this.accessToken;
  }

  async refreshAccessToken(): Promise<string> {
    // Prevent multiple simultaneous refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      throw new Error("No refresh token available");
    }

    this.refreshPromise = authApi
      .refreshToken(this.refreshToken)
      .then((response) => {
        this.setTokens(response.tokens);
        this.refreshPromise = null;
        return this.accessToken!;
      })
      .catch((error) => {
        this.refreshPromise = null;
        // Clear tokens on refresh failure
        this.clearTokens();
        throw error;
      });

    return this.refreshPromise;
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem("refreshToken");

    if (this.tokenExpiryTimeout) {
      clearTimeout(this.tokenExpiryTimeout);
      this.tokenExpiryTimeout = null;
    }
  }

  isTokenExpired(): boolean {
    if (!this.accessToken) return true;

    try {
      const payload = this.parseToken(this.accessToken);
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const bufferTime = 60 * 1000; // Refresh 1 minute before expiry

      return Date.now() >= expiryTime - bufferTime;
    } catch {
      return true;
    }
  }

  parseToken(token: string): TokenPayload {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch {
      throw new Error("Failed to parse token");
    }
  }

  getUserFromToken(): {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null {
    if (!this.accessToken) return null;

    try {
      const payload = this.parseToken(this.accessToken);
      return {
        id: payload.userId,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
      };
    } catch {
      return null;
    }
  }

  private scheduleTokenRefresh() {
    if (this.tokenExpiryTimeout) {
      clearTimeout(this.tokenExpiryTimeout);
    }

    if (!this.accessToken) return;

    try {
      const payload = this.parseToken(this.accessToken);
      const expiryTime = payload.exp * 1000;
      const refreshTime = expiryTime - Date.now() - 2 * 60 * 1000; // Refresh 2 minutes before expiry

      if (refreshTime > 0) {
        this.tokenExpiryTimeout = setTimeout(() => {
          this.refreshAccessToken().catch((error) => {
            console.error("Failed to refresh token:", error);
          });
        }, refreshTime);
      }
    } catch (error) {
      console.error("Failed to schedule token refresh:", error);
    }
  }

  // Check if user has a valid session (either valid access token or refresh token)
  async hasValidSession(): Promise<boolean> {
    if (this.accessToken && !this.isTokenExpired()) {
      return true;
    }

    if (this.refreshToken) {
      try {
        await this.refreshAccessToken();
        return true;
      } catch {
        return false;
      }
    }

    return false;
  }
}

export const authService = new AuthService();
